import { Player } from '@quickplay/player-interface';
import { AnalyticsReporterConfiguration } from '../configuration/AnalyticsReporterConfiguration';
import { Provider } from '../Provider';
import { NPAWAnalyticsReporter } from './model/NPAWAnalyticsReporter';
import { createError, createLogger, Logger, LoggerLevel, UtilFactory } from '@quickplay/foundation';
import { NPAWAnalyticsReporterConfiguration } from '../../fl-analytics';
import NpawPluginAdapters from '../../config/npaw/npawPluginAdapter';
import { AnalyticsErrorCodes } from '../AnalyticsErrorCodes';
import { PlaybackEvent } from '../../event/PlaybackEvent';

const TAG_NAME = 'FL_ANALYTICS';
const NOT_INITIALIZED = '[ANALYTICS] analytics reporter not initialized';

export class NPAWAnalyticsReporterImpl implements NPAWAnalyticsReporter {
  private deferred_: any;
  private static instance: NPAWAnalyticsReporter;
  private logger!: Logger;
  private initialized: boolean;
  private npawPluginInstance: any;
  private npawConfig?: NPAWAnalyticsReporterConfiguration;

  private constructor() {
    this.initialized = false;
  }

  static getInstance(): NPAWAnalyticsReporter {
    if (!NPAWAnalyticsReporterImpl.instance) {
      NPAWAnalyticsReporterImpl.instance = new NPAWAnalyticsReporterImpl();
    }
    return NPAWAnalyticsReporterImpl.instance;
  }

  get provider(): Provider {
    return Provider.NPAW;
  }

  initialize(configuration: AnalyticsReporterConfiguration): Promise<void> {
    this.deferred_ = new UtilFactory.Deferred<void>();
    this.logger = configuration.logger || createLogger(LoggerLevel.OFF, TAG_NAME);
    this.npawConfig = configuration as NPAWAnalyticsReporterConfiguration;

    try {
      if (
        !this.npawConfig?.accountCode ||
        typeof this.npawConfig.accountCode !== 'string' ||
        this.npawConfig.accountCode.trim() === '' ||
        !window.NpawPlugin
      ) {
        this.deferred_.reject(new Error('Invalid NPAW config or NpawPlugin missing'));
        return this.deferred_.promise;
      }

      this.npawPluginInstance = new window.NpawPlugin(
        this.npawConfig.accountCode,
        // Developer Note:
        // We’re experiencing a critical slow VST on lower-end SmartTV devices for VOD playback where it takes 30–40 seconds to start, while the same devices play live content in 3–7 seconds — that’s a 4–11x slower startup for VOD.
        // We suspect this is caused by NPAW’s CDN Balancer blocking media segment requests while waiting on the / decision call and it was fired on lower-end devices alone.
        // Upon disabling the balancer, VOD startup times are comparable to live, which indicates the balancer is likely the root cause of the slow startup.
        // After checking this with NPAW, it was recommended to disable the balancer to improve startup times on lower-end devices.
        {
          components: { balancer: false },
        },
      );
      this.initialized = true;

      // Set NPAW logger level
      this.npawPluginInstance.setLogLevel(this.npawConfig.loggerLevel);

      // Required to track events before playback adapter registration.
      this.npawPluginInstance.registerDefaultAdapter();

      let metadata: { [key: string]: any } = {};

      if (configuration.application) {
        metadata = {
          ...metadata,
          'app.name': configuration.application.name,
          'app.releaseVersion': configuration.application.version,
        };
      }

      if (configuration.device) {
        metadata = {
          ...metadata,
          'device.name': configuration.device.customDeviceName,
          'device.model': configuration.device.model,
          'device.brand': configuration.device.brand,
          'device.type': configuration.device.type,
          'device.osName': configuration.device.osName,
          'device.osVersion': configuration.device.osVersion,
        };
      }

      if (configuration.user) {
        metadata = {
          ...metadata,
          userId: this.npawConfig.shouldUseProfileIdAsUserId ? configuration.user.profileId : configuration.user.id,
          'user.type': configuration.user.type,
          'user.profileId': configuration.user.profileId,
          'content.transactionCode': configuration.user?.transactionCode,
        };
      }

      this.npawPluginInstance?.setAnalyticsOptions(this.removeUndefinedData(metadata));
      this.deferred_.resolve();
    } catch (err) {
      this.logger.error('[NPAW] Initialization failed:', err);
      const reason = this.npawConfig.accountCode
        ? 'NpawPlugin is not available'
        : 'accountCode is required and cannot be empty';
      createError(AnalyticsErrorCodes.NPAW_ANALYTICS_INITIALIZATION_FAILURE, `[NPAW] Initialization failed: ${reason}`);

      this.deferred_.reject(err);
    }

    return this.deferred_.promise;
  }

  reportEvent(eventName: string, metadata?: { [key: string]: any }, _context?: any): void {
    if (!this.isInitialized()) {
      this.logger?.log(NOT_INITIALIZED);
      return;
    }

    metadata = this.removeUndefinedData(metadata);

    // NPAW supports reporting Playback error events.
    // NPAW do not report Application events or other custom events.
    const allowedEvents = [PlaybackEvent.PLAYBACK_ERROR];
    if (!allowedEvents.includes(eventName as any)) {
      this.logger?.log(`[NPAW] Ignoring event: ${eventName}`);
      return; // Ignore all other events
    }

    const adapter = this.npawPluginInstance?.getAdapter();
    if (!adapter) {
      this.logger?.log('[NPAW] Adapter not available');
      return;
    }

    if (eventName === PlaybackEvent.PLAYBACK_ERROR) {
      const errorCode = metadata?.errorCode;
      const errorMessage = metadata?.errorMessage;

      const errorMetadata = errorCode != null && errorMessage != null ? `${errorCode} ${errorMessage}` : undefined;

      adapter.fireError(errorCode, errorMessage, errorMetadata);
    } else {
      adapter.fireEvent(eventName, metadata);
    }
  }

  reportMetadata(metadata: { [key: string]: any }, _context?: any): void {
    if (!this.isInitialized()) {
      this.logger?.log(NOT_INITIALIZED);
      return;
    }

    const profileId = metadata?.['user.profileId'];

    if (this.npawConfig?.shouldUseProfileIdAsUserId && profileId) {
      metadata.userId = profileId;
    }

    // setVideoOptions: Video options are applied only to the specific video session they are registered with.
    // Refer to the README: For a detailed explanation of the difference between video options and analytics options.
    if (this.isVideoSpecificMetadata(metadata)) {
      this.npawPluginInstance?.setVideoOptions(metadata);
      this.logger?.log('[ANALYTICS] Set NPAW video options:', metadata);
    } else {
      this.npawPluginInstance?.setAnalyticsOptions(metadata);
      this.logger?.log('[ANALYTICS] Set NPAW analytics options:', metadata);
    }
  }

  private isVideoSpecificMetadata(metadata: { [key: string]: any }): boolean {
    const videoSpecificKeys = ['content.', 'ad.', 'encoding.', 'duration', 'title', 'program', 'season', 'episode'];

    return Object.keys(metadata).some(key => videoSpecificKeys.some(prefix => key.includes(prefix)));
  }

  setCustomDimensions(
    content: { [key: number]: string | undefined },
    ad?: { [key: number]: string | undefined },
    replicateContentToAd?: boolean,
  ): void {
    if (!this.isInitialized()) {
      this.logger?.log(NOT_INITIALIZED);
      return;
    }

    const metadata: { [key: string]: string | undefined } = {};

    // Map content dimensions (1-20)
    this.mapDimensions(content, 'content', 20, metadata);

    // Replicate content to ad (1-10 only) - apply before explicit ad dimensions
    if (replicateContentToAd) {
      this.mapDimensions(content, 'ad', 10, metadata);
    }

    // Map ad dimensions (1-10) - explicit ad values override replicated content
    if (ad) {
      this.mapDimensions(ad, 'ad', 10, metadata);
    }

    this.npawPluginInstance?.setAnalyticsOptions(metadata);
  }

  private mapDimensions(
    dimensions: { [key: number]: string | undefined },
    prefix: 'content' | 'ad',
    limit: number,
    metadata: { [key: string]: string | undefined },
  ): void {
    Object.entries(dimensions).forEach(([key, value]) => {
      const keyNum = Number(key);
      if (!Number.isInteger(keyNum) || keyNum < 1 || keyNum > limit) {
        this.logger?.log(`[NPAW] Provided key ${key} is not a valid ${prefix} custom dimension`);
        return;
      }
      metadata[`${prefix}.customDimension.${key}`] = value?.trim() ? value : undefined;
    });
  }

  isInitialized(): boolean {
    return this.initialized;
  }

  reset(): void {
    this.initialized = false;
  }

  attachPlayer(player: Player): void {
    if (!this.npawPluginInstance) {
      return;
    }

    // See README for details on the three supported methods for registering NPAW adapters.

    // Registers the video adapter using the provided class reference.
    // This enables event mapping and logic for video playback analytics from NPAW Shaka Adapter.
    this.npawPluginInstance.registerAdapterFromClass(player, NpawPluginAdapters.ShakaAdapter);
    // Registers the ads adapter using the provided class reference.
    // This enables event mapping and logic for ad analytics from Custom Ads Adapter.
    this.npawPluginInstance.registerAdsAdapterFromClass(player, NpawPluginAdapters.CustomAdsAdapter);
  }

  /**
   * Removes undefined or null properties from the provided map.
   * @param metadata the map to remove undefined or null properties
   * @private
   */
  private removeUndefinedData(metadata?: { [key: string]: any }): { [p: string]: any } {
    const newMetadata = {
      ...metadata,
    };

    Object.keys(newMetadata).forEach((key: string) =>
      newMetadata[key] === undefined || newMetadata[key] === null ? delete newMetadata[key] : {},
    );

    return newMetadata;
  }
}

// Global declaration for NpawPlugin
declare global {
  interface Window {
    NpawPlugin: any;
  }
}
